import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Calendar, Check, Eye, Flag, CheckSquare, ChatCircle, LinkSimple, Clock } from '@phosphor-icons/react';
import type { Task } from '@/types';
import { fmtDate, lighten, contrastingText } from '@/lib/utils';
import { useAppStore } from '@/store/appStore';
import clsx from 'clsx';

interface Props { task: Task; }

/**
 * Day diff computation for the date badge.
 * - If completed: diff = projected - completed  (positive = early/on-time, negative = late)
 * - If not completed: diff = projected - today  (positive = days left, negative = overdue)
 * Returns null when there's no projected date to compare against.
 */
function computeDateBadge(task: Task): { label: string; color: string } | null {
  if (!task.fechaProyectada) return null;

  const projected = new Date(task.fechaProyectada);
  if (isNaN(projected.getTime())) return null;

  // Reference point: completion date if completed, else today
  const reference = task.fechaCompletado
    ? new Date(task.fechaCompletado)
    : new Date();
  if (isNaN(reference.getTime())) return null;

  // Strip time component for whole-day diff
  const projDay = Date.UTC(projected.getFullYear(), projected.getMonth(), projected.getDate());
  const refDay  = Date.UTC(reference.getFullYear(), reference.getMonth(), reference.getDate());
  const diffDays = Math.round((projDay - refDay) / 86400000);

  const isCompleted = !!task.fechaCompletado;

  // Completed exactly on time
  if (isCompleted && diffDays === 0) {
    return { label: '✓', color: '#1D9E75' }; // green
  }

  // Completed early (positive) or late (negative)
  if (isCompleted) {
    return diffDays > 0
      ? { label: `+${diffDays}d`, color: '#1D9E75' }    // green: early
      : { label: `${diffDays}d`,  color: '#D85A30' };   // orange: late
  }

  // Not completed: days left vs overdue
  return diffDays >= 0
    ? { label: `+${diffDays}d`, color: '#747B86' }      // gray: time remaining
    : { label: `${diffDays}d`,  color: '#BB4A4A' };     // red: overdue
}

export function TaskCard({ task }: Props) {
  const selectTask = useAppStore(s => s.selectTask);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.idTareaArq}`,
    data: { type: 'task', task }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  const bg = task.colorEstado ? lighten(task.colorEstado, 0.92) : '#ffffff';
  const initials = (task.nomResponsable || '?').trim()[0].toUpperCase();

  // ─── Date display logic with smart fallbacks ──────────────────────────────
  // Left slot: real start if available, else creation date (with subtle gray to signal "not yet started")
  const startDate = task.fechaInicio ?? task.fechaCreacion ?? null;
  const startIsReal = !!task.fechaInicio;

  // Right slot: completion if available, else projected (with check icon when completed)
  const endDate = task.fechaCompletado ?? task.fechaProyectada ?? null;
  const endIsCompleted = !!task.fechaCompletado;

  const dateBadge = computeDateBadge(task);

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, background: bg }}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        if (!isDragging) {
          e.stopPropagation();
          selectTask(task.idTareaArq);
        }
      }}
      className={clsx(
        'rounded-md border border-stone-200 px-2.5 py-2 cursor-grab',
        'hover:border-stone-400 hover:shadow-sm transition-all',
        'select-none text-xs'
      )}
    >
      <div className="flex items-start gap-1.5 mb-1.5">
        <Eye size={12} weight="bold" className="text-stone-400 mt-0.5 flex-shrink-0" />
        <span className="font-medium text-stone-900 leading-tight">{task.nombreTarea}</span>
      </div>

      {/* Combined status + priority chip:
          - Background = status color
          - Leading dot = priority color
          - Label = status name
          - Tooltip shows priority name (since it has no visible label) */}
      {task.nomEstado && task.colorEstado && (
        <div className="mb-1.5">
          <span
            className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded"
            style={{
              backgroundColor: task.colorEstado,
              color: contrastingText(task.colorEstado)
            }}
            title={task.nomPrioridad ? `Prioridad: ${task.nomPrioridad}` : undefined}
          >
            {task.colorPrioridad && (
              <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: task.colorPrioridad }}
                aria-hidden
              />
            )}
            {task.nomEstado}
          </span>
        </div>
      )}

      <div className="flex gap-1 mb-1.5">
  {task.nomProyecto && (
    <span className="flex-1 bg-brand-gray-100 rounded-full px-2 py-0.5 text-[10px] text-brand-gray-400 truncate text-center">
      {task.nomProyecto}
    </span>
  )}
  {task.nomCategoria && (
    <span className="flex-1 bg-brand-gray-100 rounded-full px-2 py-0.5 text-[10px] text-brand-gray-400 truncate text-center">
      {task.nomCategoria}
    </span>
  )}
</div>

      {/* Dates row:
          Left: start (real start = blue, creation fallback = gray)
          Right: end (completed = green check, projected fallback = purple flag) + day-diff badge */}
      {(startDate || endDate) && (
        <div className="flex items-center gap-2 text-[10px] text-stone-600 mb-1">
          {startDate && (
            <span
              className="flex items-center gap-0.5"
              title={startIsReal ? 'Fecha de inicio' : 'Fecha de creación (aún no iniciada)'}
            >
              <Calendar
                size={10}
                weight="bold"
                className={startIsReal ? 'text-blue-600' : 'text-stone-400'}
              />
              <span
                className={clsx(
                  'font-mono',
                  startIsReal ? 'text-blue-700' : 'text-stone-500'
                )}
              >
                {fmtDate(startDate)}
              </span>
            </span>
          )}

          {endDate && (
            <span
              className="flex items-center gap-0.5"
              title={endIsCompleted ? 'Fecha de completado' : 'Fecha proyectada'}
            >
              {endIsCompleted ? (
                <Check size={10} weight="bold" className="text-emerald-600" />
              ) : (
                <Flag size={10} weight="bold" className="text-purple-600" />
              )}
              <span
                className={clsx(
                  'font-mono',
                  endIsCompleted ? 'text-emerald-700' : 'text-purple-700'
                )}
              >
                {fmtDate(endDate)}
              </span>
            </span>
          )}

          {dateBadge && (
            <span
              className="font-mono font-medium ml-auto"
              style={{ color: dateBadge.color }}
              title={endIsCompleted
                ? 'Diferencia entre fecha proyectada y completado (+ adelanto, − retraso)'
                : 'Días restantes hasta fecha proyectada (− vencido)'}
            >
              {dateBadge.label}
            </span>
          )}
        </div>
      )}

      {/* Footer: checklist counters + comment count + horas + assignee */}
      <div className="flex items-center justify-between mt-1 text-[10px]">
        <div className="flex items-center gap-2">
          {task.checklistTotal > 0 && (
            <span className="flex items-center gap-0.5 text-stone-500 font-mono">
              <CheckSquare size={10} weight="bold" />{task.checklistDone}/{task.checklistTotal}
            </span>
          )}
          {task.commentCount > 0 && (
            <span className="flex items-center gap-0.5 text-stone-500 font-mono">
              <ChatCircle size={10} weight="bold" />{task.commentCount}
            </span>
          )}
          {task.horasEstimadas != null && (
            <span
              className="flex items-center gap-0.5 text-stone-600 font-mono"
              title="Horas estimadas"
            >
              <Clock size={10} weight="bold" />
              {task.horasEstimadas % 1 === 0 ? task.horasEstimadas : task.horasEstimadas.toFixed(1)}h
            </span>
          )}
          {task.linkEntrega && (
            // Quick-open de la entrega. stopPropagation evita que el click
            // burbujee y abra el modal de la tarea por encima.
            <a
              href={task.linkEntrega}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              title="Abrir link de entrega"
              className="flex items-center text-brand-green-200 hover:text-brand-green-100"
            >
              <LinkSimple size={12} weight="bold" />
            </a>
          )}
        </div>
        {task.nomResponsable && (
          <span className="flex items-center gap-1 text-stone-700">
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-medium"
              style={{ background: '#378ADD' }}
            >
              {initials}
            </span>
            {task.nomResponsable}
          </span>
        )}
      </div>
    </div>
  );
}